import { apiUrl } from "@2skies/frontend-shared";
import type { TapeDelta, TickerTapeSnapshot } from "../types.js";
import { SCREEN_ID } from "./screenId.js";

export interface TapeStreamHandlers {
  onPane: (snap: TickerTapeSnapshot) => void;
  onDelta?: (d: TapeDelta) => void;
  onReady?: () => void;
  onError?: () => void;
  /** This account opened DeltaTape on another screen; the stream is already closed. */
  onDisplaced?: () => void;
}

/**
 * Open the DeltaTape SSE stream for a symbol. EventSource can't set headers, so
 * the Discord access token rides as a query param (the backend copies it to the
 * Authorization header). Inside Discord the path is relative (Discord URL
 * mapping); in a browser tab `apiUrl` prefixes the backend host. Returns a disposer that closes the stream.
 */
export function openTapeStream(
  token: string,
  symbol: string,
  handlers: TapeStreamHandlers,
  /** `resume`: a hidden tab coming back — the server refuses (`displaced`) rather than kick another live screen. */
  opts: { resume?: boolean } = {},
): () => void {
  const url = apiUrl(
    `/api/deltatape/stream?token=${encodeURIComponent(token)}&symbol=${encodeURIComponent(symbol)}&screen=${encodeURIComponent(SCREEN_ID)}&v=2${opts.resume ? "&resume=1" : ""}`,
  );
  const es = new EventSource(url);
  es.addEventListener("ready", () => handlers.onReady?.());
  es.addEventListener("pane", (ev) => {
    try {
      handlers.onPane(JSON.parse((ev as MessageEvent).data) as TickerTapeSnapshot);
    } catch {
      handlers.onError?.();
    }
  });
  es.addEventListener("delta", (ev) => {
    try {
      handlers.onDelta?.(JSON.parse((ev as MessageEvent).data) as TapeDelta);
    } catch {
      handlers.onError?.();
    }
  });
  // Bumped by a newer screen on this account. Close FIRST so EventSource doesn't
  // auto-reconnect (which would bump the other screen straight back).
  es.addEventListener("displaced", () => {
    es.close();
    handlers.onDisplaced?.();
  });
  es.onerror = () => handlers.onError?.();
  return () => es.close();
}
